import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';

import { ICardInfo } from '../../../_shared/models/card/card-info.model';
import { ICardComponent } from '../../../_shared/models/card/card.model';
import { IChip } from '../../../_shared/models/chip.model';
import { ImageData } from '../../../_shared/models/image/image-data.model';
import { EMPTY_LINK, ILink } from '../../../_shared/models/link.model';
import { MediaImage } from '../../../_shared/models/media/media-image.model';
import { LinkService } from '../../../_shared/services/link.service';
import { SnowplowService } from '../../../_shared/services/snowplow.service';
import { CardContainer } from '../../../_shared/types/card/card-container.type';
import { CardCatalogueDataSource } from '../../../_shared/types/card-grid-data-source.type';
import {
    BlogPost,
    CaseStory,
    createBlogPost,
    createCaseStory,
    createNewsArticle,
    NewsArticle,
} from '../../../_shared/types/snowtype/snowplow';
import { withConfigFallback } from '../../../_shared/utils/with-config-fallback';

import { CardArticleComponent } from './card-article/card-article.component';
import { CardCustomComponent } from './card-custom/card-custom.component';

@Component({
    selector: 'sirius-card-info',
    templateUrl: './card-info.component.html',
    styleUrls: ['card-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CardArticleComponent, CardCustomComponent],
})
export class CardInfoComponent implements ICardComponent<ICardInfo> {
    private _snowplowService = inject(SnowplowService);
    private _linkService = inject(LinkService);

    readonly config = input<ICardInfo | undefined>(undefined);
    readonly imageData = input<ImageData | undefined>(undefined);
    readonly dataSource = input<CardCatalogueDataSource>('other');
    readonly container = input<CardContainer>('slider');

    // Public inputs keep their natural API name. Consumers bind `[title]`, `[image]`, etc.
    readonly cardType = input<ICardInfo['cardType']>('custom');
    readonly title = input('');
    readonly textHtml = input('');
    readonly tags = input<IChip[]>([]);
    readonly attributes = input<IChip[]>([]);
    readonly image = input<MediaImage | undefined>(undefined);
    readonly date = input<string | undefined>(undefined);
    readonly publishedAt = input('');
    readonly contentType = input('');
    readonly link = input<ILink>(EMPTY_LINK);

    // Internal derived signals read by this component's own template only.
    protected readonly resolvedCardType = withConfigFallback<ICardInfo, 'cardType', ICardInfo['cardType']>(this.config, 'cardType', this.cardType);
    protected readonly resolvedTitle = withConfigFallback<ICardInfo, 'title', string>(this.config, 'title', this.title);
    protected readonly resolvedTextHtml = withConfigFallback<ICardInfo, 'textHtml', string>(this.config, 'textHtml', this.textHtml);
    protected readonly resolvedTags = withConfigFallback<ICardInfo, 'tags', IChip[]>(this.config, 'tags', this.tags);
    protected readonly resolvedAttributes = withConfigFallback<ICardInfo, 'attributes', IChip[]>(this.config, 'attributes', this.attributes);
    protected readonly resolvedImage = withConfigFallback<ICardInfo, 'image'>(this.config, 'image', this.image);
    protected readonly resolvedDate = withConfigFallback<ICardInfo, 'date'>(this.config, 'date', this.date);
    protected readonly resolvedPublishedAt = withConfigFallback<ICardInfo, 'publishedAt', string>(this.config, 'publishedAt', this.publishedAt);
    protected readonly resolvedContentType = withConfigFallback<ICardInfo, 'contentType', string>(this.config, 'contentType', this.contentType);
    protected readonly resolvedLink = withConfigFallback<ICardInfo, 'link', ILink>(this.config, 'link', this.link);

    readonly selectEvent = output<void>();

    onSelect(): void {
        this.trackLinkClick();
        this.selectEvent.emit();
    }

    private trackLinkClick(): void {
        const title = this.resolvedTitle();
        const tags = this.resolvedTags();
        const link = this.resolvedLink();
        const trackingContext: unknown[] = [];

        switch (this.dataSource()) {
            case 'blogs': {
                const blogPost: BlogPost = {
                    name: title,
                    author: '',
                    category: tags.map((tag: IChip) => tag.text),
                };
                trackingContext.push(createBlogPost(blogPost));
                break;
            }
            case 'blogsOnlyNews': {
                const newsArticle: NewsArticle = {
                    title,
                    category: tags.map((tag: IChip) => tag.text),
                };
                trackingContext.push(createNewsArticle(newsArticle));
                break;
            }
            case 'caseStories': {
                const caseStory: CaseStory = { name: title };
                trackingContext.push(createCaseStory(caseStory));
                break;
            }
        }

        this._snowplowService.track('linkClick', {
            category: 'card',
            container: this.container(),
            identifier: link.tracking?.parameters['identifier'],
            target_url: this._linkService.getFullPath(link.href),
            element_content: link.title,
            outbound: link.target === '_blank',
            context: trackingContext,
        });
    }
}
